import AuthenticatedPaths from '/b2b/routing/AuthenticatedRoutes/AuthenticatedRoutes.paths';
// Import the flag constants from the leaf, not the barrel: the barrel also
// re-exports the FeatureFlag component + container, which pulls the whole
// selector graph back to this constants module and forms an import cycle.
import { ENABLE_CMP_DASHBOARD } from '/b2b/common/components/FeatureFlag/constants';
import { PERMISSIONS } from '/b2b/authentication/constants';

export const NAV_LABELS = {
    assessments: 'layouts.Dashboard.components.Sidebar.assessments.label',
    complianceCheck: 'layouts.Dashboard.components.Sidebar.complianceCheck.label',
    consentManager: 'views.ConsentManager.title',
    consultPrivacyTeam: 'layouts.Dashboard.components.Sidebar.consultPrivacyTeam.label',
    dashboard: 'layouts.Dashboard.components.Sidebar.dashboard.label',
    dataDiscovery: 'layouts.Dashboard.components.Sidebar.dataDiscovery.label',
    dataMapping: 'layouts.Dashboard.components.Sidebar.dataMapping.label',
    dsar: 'layouts.Dashboard.components.Sidebar.dataSubjectRequests.label',
    gdprRepresentative: 'layouts.Dashboard.components.Sidebar.gdprRepresentative.label',
    integrations: 'layouts.Dashboard.components.Sidebar.integrations.label',
    privacyAdvisor: 'layouts.Dashboard.components.Sidebar.privacyAdvisor.label',
    privacyTemplates: 'layouts.Dashboard.components.Sidebar.templates.label',
    regulatoryGuidance: 'layouts.Dashboard.components.Sidebar.regulatoryGuidance.label',
    trusthub: 'layouts.Dashboard.components.Sidebar.trusthub.label',
    unifiedConsent: 'title.unifiedConsent',
    vendor: 'layouts.Dashboard.components.Sidebar.vendorManagement.label',
};

export const SUBMENU_LABELS = {
    assessmentAssignments: 'layouts.Dashboard.components.Sidebar.assessmentAssignments.label',
    assessmentReporting: 'layouts.Dashboard.components.Sidebar.reporting.label',
    assessmentTemplates: 'layouts.Dashboard.components.Sidebar.assessmentTemplates.label',
    commonConfigurations: 'common.configurations',
    commonReporting: 'common.reporting',
    dataApplications: 'layouts.Dashboard.components.Sidebar.dataMapping.submenu.applications.label',
    dataMap: 'layouts.Dashboard.components.Sidebar.dataMapping.submenu.dataMap.label',
    dataProviders: 'layouts.Dashboard.components.Sidebar.dataMapping.submenu.dataProviders.label',
    dataSources: 'layouts.Dashboard.components.Sidebar.dataMapping.submenu.dataSources.label',
    dsarActionItems:
        'layouts.Dashboard.components.Sidebar.dataSubjectRequests.submenu.actionItemList.label',
    dsarForms:
        'layouts.Dashboard.components.Sidebar.dataSubjectRequests.submenu.requestSubmissionForms.label',
    dsarRequests:
        'layouts.Dashboard.components.Sidebar.dataSubjectRequests.submenu.requestSubmissions.label',
    ucConfigurations: 'common.configurations',
    consumerSso: 'title.consumerSso',
    ucIntegrations: 'uc.title.integrations',
    ucProtocols: 'uc.title.protocols',
    vmPolicyChanges: 'layouts.Dashboard.components.Sidebar.policyChanges.label',
    vmVendors: 'layouts.Dashboard.components.Sidebar.vendors.label',
};

/** Defines base DSAR submenu pages before plan filtering. */
const dsarNavPages = [
    {
        title: SUBMENU_LABELS.dsarForms,
        path: AuthenticatedPaths.DSAR_FORMS,
        permission: {
            action: 'read',
            type: 'on',
            permissions: [PERMISSIONS.DSAR_REQUEST],
        },
        featureFlag: null,
    },
    {
        title: SUBMENU_LABELS.dsarActionItems,
        path: AuthenticatedPaths.DSAR_ACTION_ITEMS,
        permission: {
            action: 'read',
            type: 'on',
            permissions: [PERMISSIONS.DSAR_DATASOURCE],
        },
        featureFlag: null,
    },
    {
        title: SUBMENU_LABELS.dsarRequests,
        path: AuthenticatedPaths.DSAR_REQUESTS,
        permission: {
            action: 'read',
            type: 'on',
            permissions: [PERMISSIONS.DSAR_REQUEST],
        },
        featureFlag: null,
    },
    {
        title: SUBMENU_LABELS.consumerSso,
        path: AuthenticatedPaths.DSAR_CONSUMER_SSO,
        permission: {
            action: 'read',
            type: 'on',
            permissions: [PERMISSIONS.DSAR_REQUEST],
        },
        featureFlag: null,
    },
    {
        title: SUBMENU_LABELS.commonReporting,
        path: AuthenticatedPaths.DSAR_REPORTING,
        permission: {
            action: 'read',
            type: 'on',
            permissions: [PERMISSIONS.DSAR_REQUEST],
        },
        featureFlag: null,
    },
];

/** Returns DSAR submenu pages filtered by plan entitlements. */
export const getDsarNavPages = ({ canViewDsarConsumerSso = true } = {}) => {
    return dsarNavPages.filter(({ path }) => {
        if (path === AuthenticatedPaths.DSAR_CONSUMER_SSO) {
            return canViewDsarConsumerSso;
        }
        return true;
    });
};

/** Returns sidebar menu config with DSAR visibility rules. */
export const getNavMenuItems = ({
    canViewDsarConsumerSso = true,
    isDataStoresTopLevelEnabled = false,
    isVendorV2Enabled = false,
} = {}) => ({
    [NAV_LABELS.consentManager]: {
        featureFlag: ENABLE_CMP_DASHBOARD,
        pages: [
            {
                title: SUBMENU_LABELS.commonConfigurations,
                path: AuthenticatedPaths.CONSENT_MANAGER,
                permission: null,
                featureFlag: null,
            },
            {
                title: SUBMENU_LABELS.commonReporting,
                path: AuthenticatedPaths.CONSENT_DASHBOARD,
                permission: null,
                featureFlag: ENABLE_CMP_DASHBOARD,
            },
        ],
    },
    [NAV_LABELS.unifiedConsent]: {
        pages: [
            {
                title: SUBMENU_LABELS.ucProtocols,
                path: AuthenticatedPaths.UC_PRIVACY_PROTOCOLS,
                permission: null,
                featureFlag: null,
            },
            {
                title: SUBMENU_LABELS.commonConfigurations,
                path: AuthenticatedPaths.UC_CONFIGS,
                permission: null,
                featureFlag: null,
            },
            {
                title: SUBMENU_LABELS.consumerSso,
                path: AuthenticatedPaths.CONSUMER_SSO,
                permission: null,
                featureFlag: null,
            },
            {
                title: SUBMENU_LABELS.ucIntegrations,
                path: AuthenticatedPaths.UC_INTEGRATIONS,
                permission: null,
                featureFlag: null,
            },
            {
                title: SUBMENU_LABELS.commonReporting,
                path: AuthenticatedPaths.UC_DASHBOARD,
                permission: null,
                featureFlag: null,
            },
        ],
    },
    [NAV_LABELS.dsar]: {
        pages: getDsarNavPages({
            canViewDsarConsumerSso,
        }),
    },
    [NAV_LABELS.dataMapping]: {
        pages: [
            {
                title: SUBMENU_LABELS.dataSources,
                path: AuthenticatedPaths.DATA_MAPPING_SOURCES,
                permission: null,
                featureFlag: null,
                rolePermissions: ['canReadDataMapping'],
            },
            {
                title: SUBMENU_LABELS.dataApplications,
                path: AuthenticatedPaths.DATA_MAPPING_APPLICATIONS,
                permission: null,
                featureFlag: null,
                rolePermissions: ['canReadDataMapping'],
            },
            // When Data Stores is promoted to a top-level item, it leaves the
            // Data Mapping flyout; otherwise it remains here (minified menu).
            ...(isDataStoresTopLevelEnabled
                ? []
                : [
                      {
                          title: SUBMENU_LABELS.dataProviders,
                          path: AuthenticatedPaths.DATA_STORES,
                          permission: null,
                          featureFlag: null,
                          rolePermissions: ['canReadDataMapping', 'canManageSubjectRights'],
                      },
                  ]),
            {
                title: SUBMENU_LABELS.dataMap,
                path: AuthenticatedPaths.DATA_MAP,
                permission: null,
                featureFlag: null,
                rolePermissions: ['canReadDataMapping'],
            },
        ],
    },
    [NAV_LABELS.vendor]: {
        pages: [
            {
                title: SUBMENU_LABELS.vmVendors,
                path: AuthenticatedPaths.VENDORS,
                permission: null,
                featureFlag: null,
            },
            ...(isVendorV2Enabled
                ? []
                : [
                      {
                          title: SUBMENU_LABELS.vmPolicyChanges,
                          path: AuthenticatedPaths.DOCUMENT_CHANGES,
                          permission: null,
                          featureFlag: null,
                      },
                  ]),
        ],
    },
    [NAV_LABELS.assessments]: {
        pages: [
            {
                title: NAV_LABELS.assessments,
                path: AuthenticatedPaths.ASSESSMENTS,
                activePathExclusions: [
                    AuthenticatedPaths.ASSESSMENT_TEMPLATES,
                    AuthenticatedPaths.ASSESSMENT_ASSIGNMENTS,
                    AuthenticatedPaths.ASSESSMENTS_REPORTING,
                ],
                permission: {
                    action: 'read',
                    type: 'onAny',
                    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.ASSESSMENT],
                },
                featureFlag: null,
            },
            {
                title: SUBMENU_LABELS.assessmentTemplates,
                path: AuthenticatedPaths.ASSESSMENT_TEMPLATES,
                permission: {
                    action: 'read',
                    type: 'onAny',
                    permissions: [PERMISSIONS.ADMIN, PERMISSIONS.ASSESSMENT],
                },
                featureFlag: null,
            },
            {
                title: SUBMENU_LABELS.assessmentAssignments,
                path: AuthenticatedPaths.ASSESSMENT_ASSIGNMENTS,
                permission: null,
                featureFlag: null,
            },
            {
                title: SUBMENU_LABELS.assessmentReporting,
                path: AuthenticatedPaths.ASSESSMENTS_REPORTING,
                permission: {
                    action: 'read',
                    type: 'on',
                    permissions: [PERMISSIONS.ASSESSMENT],
                },
                featureFlag: null,
            },
        ],
    },
});

export const navMenuItems = getNavMenuItems();
